// hooks/usePriceCalculations.ts
import { useState, useCallback, useEffect } from 'react'

interface UsePriceCalculationsProps {
  activeTab: string
  price: string
  lastPrice: number | null
}

/**
 * Custom hook for handling price calculations between amount and total
 * Manages the bidirectional relationship and prevents circular updates
 */
export const usePriceCalculations = ({ activeTab, price, lastPrice }: UsePriceCalculationsProps) => {
  const [amount, setAmount] = useState('')
  const [total, setTotal] = useState('')
  const [isUpdatingFromTotal, setIsUpdatingFromTotal] = useState(false)
  const [isUpdatingFromAmount, setIsUpdatingFromAmount] = useState(false)
  const [isEditingTotal, setIsEditingTotal] = useState(false)
  const [lastSetField, setLastSetField] = useState<'amount' | 'total' | null>(null)

  // Helper function to get the appropriate price for calculations
  const getCalculationPrice = useCallback(() => {
    // For limit and stop orders, use the entered price if available
    if (activeTab === 'Limit' && price && parseFloat(price) > 0) {
      return parseFloat(price)
    }
    if (activeTab === 'Stop' && price && parseFloat(price) > 0) {
      return parseFloat(price)
    }
    // Fallback to LTP for market orders or when no limit price is entered
    return lastPrice
  }, [activeTab, price, lastPrice])

  // Calculate total from amount and appropriate price
  const calculateTotal = useCallback((amountValue: string, priceOverride?: number | null) => {
    const calculationPrice = priceOverride ?? getCalculationPrice()
    if (!amountValue || !calculationPrice || isNaN(parseFloat(amountValue))) {
      return ''
    }
    const total = parseFloat(amountValue) * calculationPrice
    // Format to remove unnecessary trailing zeros
    return parseFloat(total.toFixed(8)).toString()
  }, [getCalculationPrice])

  // Calculate amount from total and appropriate price
  const calculateAmount = useCallback((totalValue: string, priceOverride?: number | null) => {
    const calculationPrice = priceOverride ?? getCalculationPrice()
    if (!totalValue || !calculationPrice || isNaN(parseFloat(totalValue)) || calculationPrice === 0) {
      return ''
    }
    const amount = parseFloat(totalValue) / calculationPrice
    // Format to remove unnecessary trailing zeros
    return parseFloat(amount.toFixed(8)).toString()
  }, [getCalculationPrice])

  // Wrapped setters to track which field was last set
  const setAmountWithTracking = useCallback((value: string) => {
    setAmount(value)
    setLastSetField('amount')
  }, [])

  const setTotalWithTracking = useCallback((value: string) => {
    setTotal(value)
    setLastSetField('total')
  }, [])

  // Handle amount input change
  const handleAmountChange = useCallback((value: string) => {
    if (isUpdatingFromTotal) return // Prevent circular updates
    
    setAmountWithTracking(value)
    setIsUpdatingFromAmount(true)
    
    // Calculate and update total using appropriate price (limit price or LTP)
    const calculationPrice = getCalculationPrice()
    if (calculationPrice && calculationPrice > 0) {
      const newTotal = calculateTotal(value)
      setTotal(newTotal) // Direct setter to avoid changing lastSetField
    }
    
    setTimeout(() => setIsUpdatingFromAmount(false), 0)
  }, [isUpdatingFromTotal, calculateTotal, getCalculationPrice, setAmountWithTracking])

  // Handle total input change
  const handleTotalChange = useCallback((value: string) => {
    if (isUpdatingFromAmount) return // Prevent circular updates
    
    setTotalWithTracking(value)
    setIsUpdatingFromTotal(true)
    
    // Calculate and update amount using appropriate price (limit price or LTP)
    const calculationPrice = getCalculationPrice()
    if (calculationPrice && calculationPrice > 0) {
      const newAmount = calculateAmount(value)
      setAmount(newAmount) // Direct setter to avoid changing lastSetField
    }
    
    setTimeout(() => setIsUpdatingFromTotal(false), 0)
  }, [isUpdatingFromAmount, calculateAmount, getCalculationPrice, setTotalWithTracking])

  // Auto-update calculations based on which field was last set
  const calculationPrice = getCalculationPrice()
  
  useEffect(() => {
    if (!calculationPrice) return
    
    // Calculate total from amount (when amount was last set)
    if (lastSetField === 'amount' && amount && !isUpdatingFromTotal && !isEditingTotal) {
      const newTotal = calculateTotal(amount)
      if (total !== newTotal) {
        setTotal(newTotal) // Use direct setter to avoid changing lastSetField
      }
    }
    // Calculate amount from total (when total was last set)
    else if (lastSetField === 'total' && total && !isUpdatingFromAmount) {
      const newAmount = calculateAmount(total)
      if (amount !== newAmount && newAmount) {
        setAmount(newAmount) // Use direct setter to avoid changing lastSetField
      }
    }
  }, [calculationPrice, amount, total, lastSetField, isUpdatingFromTotal, isUpdatingFromAmount, isEditingTotal, calculateTotal, calculateAmount])

  // Reset form when needed
  const resetForm = useCallback(() => {
    setAmount('')
    setTotal('')
    setLastSetField(null)
  }, [])

  return {
    amount,
    total,
    setAmount: setAmountWithTracking,
    setTotal: setTotalWithTracking,
    handleAmountChange,
    handleTotalChange,
    isEditingTotal,
    setIsEditingTotal,
    isUpdatingFromTotal,
    isUpdatingFromAmount,
    calculateTotal,
    calculateAmount,
    resetForm
  }
}